import { Title } from '@solidjs/meta'
import { A, useParams } from '@solidjs/router'
import { type Component, createMemo, createResource, createSignal, For, Show } from 'solid-js'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Chip from '~/components/Chip'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import ImageViewer from '~/components/ImageViewer'
import CartDrawer from '~/layers/CartDrawer'
import QuoteRequestModal from '~/layers/QuoteRequestModal'
import { type Path, useNavigate } from '~/router'
import { activeTier, catalogService, findVariant, optionsLabel, type PublicProductType, variantPrice } from '~/services/catalog.service'
import { specLabel } from '~/services/product.service'
import { addToCart, type CartLineType, cartCount, setBuyNow } from '~/stores/cart'

const money = (value: number) => `$${value.toLocaleString('es-CO')}`

const discountPercent = (original: number, current: number): number => {
  if (original <= 0 || current <= 0 || current >= original) return 0
  return Math.round(((original - current) / original) * 100)
}

const ProductPublicPage: Component = () => {
  const params = useParams()
  const navigate = useNavigate()
  const empresa = () => params.empresa ?? ''
  const productKey = () => params.key ?? ''

  const [documentTitle, setDocumentTitle] = createSignal('Catálogo')

  const [data] = createResource(
    () => ({ empresa: empresa(), key: productKey() }),
    async ({ empresa, key }) => {
      try {
        const result = await catalogService.get(empresa, key)
        setDocumentTitle(`${result.product.name} | ${result.company.name}`)
        return result
      } catch {
        return null
      }
    }
  )

  const product = () => data()?.product
  const isService = () => product()?.kind === 'service'

  // ── Galería ──
  const [imageIndex, setImageIndex] = createSignal(0)
  const [viewerOpen, setViewerOpen] = createSignal(false)
  const images = createMemo(() => product()?.images ?? [])
  const currentImage = () => images()[Math.min(imageIndex(), Math.max(0, images().length - 1))]
  const viewerImages = createMemo(() => images().map(src => ({ src, alt: product()?.name ?? 'Imagen' })))

  // ── Variantes ──
  const [selection, setSelection] = createSignal<Record<string, string>>({})
  const selectedVariant = createMemo(() => {
    const p = product()
    return p ? findVariant(p, selection()) : undefined
  })
  // ── Cantidad (declarada antes para que el precio sea reactivo a ella) ──
  const [quantity, setQuantity] = createSignal(1)
  const step = (delta: number) => setQuantity(q => Math.max(1, Math.min(9999, q + delta)))

  const price = createMemo(() => {
    const p = product()
    if (!p) return 0
    return variantPrice(p, selectedVariant(), quantity())
  })
  const tier = createMemo(() => {
    const p = product()
    if (!p) return undefined
    return activeTier(p, selectedVariant(), quantity())
  })
  const baseUnitPrice = createMemo(() => {
    const p = product()
    if (!p) return 0
    return variantPrice(p, selectedVariant(), 1)
  })
  const totalPrice = createMemo(() => price() * quantity())
  const tiers = createMemo(() => {
    const p = product()
    if (!p?.prices?.length) return []
    const general = p.prices.filter(pr => pr.variantId == null).sort((a, b) => a.minQuantity - b.minQuantity)
    return general
  })
  const nextTier = createMemo(() => {
    const t = tiers()
    const q = quantity()
    return t.find(pr => pr.minQuantity > q)
  })
  const missingSelection = createMemo(() => {
    const p = product()
    if (!p || p.attributes.length === 0) return false
    return p.attributes.some(a => !selection()[a.name])
  })
  const stock = createMemo(() => {
    const p = product()
    if (!p?.trackInventory) return null
    const variant = selectedVariant()
    if (variant) return variant.stock
    if (p.variants.length > 0) return p.variants.reduce((sum, v) => sum + v.stock, 0)
    return null
  })

  // (cantidad movida arriba para que `price` reaccione a `quantity`)

  // ── Solicitud ──
  const [showQuote, setShowQuote] = createSignal(false)
  const [sentNumber, setSentNumber] = createSignal<string>()

  const whatsappHref = createMemo(() => {
    const p = product()
    const numero = data()?.company.whatsapp
    if (!p || !numero) return null
    const variant = selectedVariant()
    const detalle = variant ? ` (${optionsLabel(variant)})` : ''
    const texto = `Hola, me interesa ${p.name}${detalle}. ¿Me pueden dar más información?`
    return `https://wa.me/${numero}?text=${encodeURIComponent(texto)}`
  })

  // ── Compra ──
  const [showCart, setShowCart] = createSignal(false)
  const [avisoStock, setAvisoStock] = createSignal(false)

  /** Sin existencias no se puede comprar; un servicio no las controla. */
  const sinExistencias = createMemo(() => stock() != null && stock()! <= 0)
  const puedeComprar = createMemo(() => !missingSelection() && !sinExistencias())

  /** La línea tal como se guarda: lo justo para pintarla sin volver a pedir nada. */
  const buildLine = (): CartLineType | null => {
    const p = product()
    if (!p) return null
    const variant = selectedVariant()
    return {
      productId: p.id,
      variantId: variant?.id ?? null,
      key: p.key,
      name: p.name,
      variantLabel: variant ? optionsLabel(variant) : undefined,
      unitPrice: price(),
      currency: 'COP',
      image: images()[0] ?? null,
      quantity: quantity(),
      stock: stock() ?? 0,
      trackInventory: Boolean(p.trackInventory)
    }
  }

  const handleAddToCart = () => {
    const line = buildLine()
    if (!line) return
    // Si no cabía todo lo pedido, el carrito recorta al stock y se avisa: es
    // mejor decirlo aquí que dejar que el pago falle al final.
    const completo = addToCart(empresa(), line)
    setAvisoStock(!completo)
    setShowCart(true)
  }

  /**
   * "Comprar" no toca el carrito: quien llega con cosas apartadas debe
   * encontrarlas igual al volver de la pasarela.
   */
  const handleBuyNow = () => {
    const line = buildLine()
    if (!line) return
    setBuyNow({ empresa: empresa(), line })
    navigate(`/catalogo/${empresa()}/pago?directo=1` as Path)
  }

  return (
    <>
      <Title>{documentTitle()}</Title>

      <Show
        when={data() && product()}
        fallback={
          <Container class="py-20">
            <Show
              when={!data.loading}
              fallback={
                <div class="flex justify-center py-20">
                  <Icon icon="material-symbols-light:progress-activity" size={40} class="animate-spin text-foreground-subtle" />
                </div>
              }
            >
              <div class="text-center py-20">
                <Icon icon="material-symbols-light:search-off" size={48} class="text-foreground-subtle" />
                <h1 class="text-2xl font-bold text-foreground mt-4">No encontramos esto</h1>
                <p class="text-foreground-muted mt-2">El enlace puede haber cambiado o el producto ya no está disponible.</p>
                <A href={`/catalogo/${empresa()}`} class="inline-block mt-6">
                  <Button color="primary" prependIcon="material-symbols-light:arrow-back">
                    Ver el catálogo
                  </Button>
                </A>
              </div>
            </Show>
          </Container>
        }
      >
        <Container class="py-6">
          {/* Breadcrumb */}
          <div class="flex items-center justify-between mb-4">
            <nav class="flex items-center gap-1.5 text-xs text-foreground-subtle flex-wrap">
              <A href={`/catalogo/${empresa()}`} class="hover:text-primary transition-colors">
                Catálogo
              </A>
              <Show when={product()!.category}>
                <Icon icon="material-symbols-light:chevron-right" size={14} />
                <A href={`/catalogo/${empresa()}?categoria=${product()!.category!.id}`} class="hover:text-primary transition-colors">
                  {product()!.category!.label}
                </A>
              </Show>
              <Icon icon="material-symbols-light:chevron-right" size={14} />
              <span class="text-foreground font-medium">{product()!.name}</span>
            </nav>
          </div>

          {/* ── Mobile sticky bar (solo visible en móvil) ── */}
          <div class="lg:hidden sticky top-13 z-40 bg-surface border-b border-border px-4 py-3 -mx-4 mt-2">
            <p class="text-sm font-semibold text-foreground truncate">{product()!.name}</p>
            <div class="flex items-center gap-2">
              <span class="text-lg font-bold text-foreground">{money(price())}</span>
              <Show when={product()!.compareAtPrice && product()!.compareAtPrice! > price()}>
                <span class="text-xs font-semibold text-success">{discountPercent(product()!.compareAtPrice!, price())}% OFF</span>
              </Show>
            </div>
          </div>

          {/* ── 2 columnas: galería + contenido (izq) + buy box + seller (der, sticky) ── */}
          <div class="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 lg:gap-8">
            {/* ── Izquierda: galería + información del producto ── */}
            <div class="space-y-8">
              {/* Galería */}
              <div class="flex gap-3">
                <Show when={images().length > 1}>
                  <div class="hidden md:flex flex-col gap-2 w-16 shrink-0 h-[440px] overflow-hidden">
                    <For
                      each={(() => {
                        const imgs = images()
                        const MAX = 6
                        if (imgs.length <= MAX) return imgs.map((src, i) => ({ src, idx: i, overlay: null as string | null }))
                        const first = imgs.slice(0, MAX - 1).map((src, i) => ({ src, idx: i, overlay: null as string | null }))
                        const remaining = imgs.length - (MAX - 1)
                        return [...first, { src: imgs[MAX - 1], idx: MAX - 1, overlay: `+${remaining}` }]
                      })()}
                    >
                      {item => (
                        <button
                          type="button"
                          onClick={() => {
                            setImageIndex(item.idx)
                            setViewerOpen(true)
                          }}
                          class={`relative shrink-0 w-16 h-16 rounded-lg border overflow-hidden transition-all ${item.idx === imageIndex() ? 'border-primary ring-1 ring-primary/30 opacity-100' : 'border-border opacity-60 hover:opacity-100'}`}
                          aria-label={`Imagen ${item.idx + 1}`}
                        >
                          <img src={item.src} alt="" class="w-full h-full object-cover" />
                          <Show when={item.overlay}>
                            <span class="absolute inset-0 flex items-center justify-center bg-black/60 text-sm font-bold text-white">{item.overlay}</span>
                          </Show>
                        </button>
                      )}
                    </For>
                  </div>
                </Show>
                <div class="relative flex-1 bg-surface rounded-xl border border-border overflow-hidden h-80 md:h-[440px] w-full">
                  <Show
                    when={currentImage()}
                    fallback={
                      <div class="flex h-full w-full items-center justify-center">
                        <Icon icon={isService() ? 'material-symbols-light:handyman-outline' : 'material-symbols-light:inventory-2-outline'} size={80} class="text-foreground-subtle/30" />
                      </div>
                    }
                  >
                    <button type="button" class="h-full w-full cursor-zoom-in" onClick={() => setViewerOpen(true)} aria-label="Ampliar imagen">
                      <img src={currentImage()} alt={product()!.name} class="h-full w-full object-cover" />
                    </button>
                  </Show>
                  <Show when={images().length > 1}>
                    <Button
                      class="absolute left-2 top-1/2 -translate-y-1/2 shadow-md bg-white/80 backdrop-blur-sm"
                      variant="contained"
                      color="default"
                      size="sm"
                      icon="material-symbols-light:chevron-left"
                      aria-label="Anterior"
                      onClick={() => setImageIndex(i => (i - 1 + images().length) % images().length)}
                    />
                    <Button
                      class="absolute right-2 top-1/2 -translate-y-1/2 shadow-md bg-white/80 backdrop-blur-sm"
                      variant="contained"
                      color="default"
                      size="sm"
                      icon="material-symbols-light:chevron-right"
                      aria-label="Siguiente"
                      onClick={() => setImageIndex(i => (i + 1) % images().length)}
                    />
                  </Show>
                  <Show when={product()!.compareAtPrice && product()!.compareAtPrice! > price()}>
                    <span class="absolute top-3 left-3 bg-success text-white text-xs font-bold px-2 py-0.5 rounded-md shadow-sm">{discountPercent(product()!.compareAtPrice!, price())}% OFF</span>
                  </Show>
                </div>
              </div>

              <Show when={product()!.details.length > 0}>
                <div>
                  <h2 class="text-sm font-semibold text-foreground mb-2">{isService() ? '¿Qué incluye?' : 'Lo que tienes que saber'}</h2>
                  <ul class="text-sm text-foreground-muted space-y-1">
                    <For each={product()!.details}>{detail => <li>• {detail}</li>}</For>
                  </ul>
                </div>
              </Show>

              <Show when={product()!.specs.length > 0}>
                <div>
                  <h2 class="text-sm font-semibold text-foreground mb-3">Características del producto</h2>
                  <Card class="!rounded-xl">
                    <Card.Body class="p-0">
                      <dl class="divide-y divide-border">
                        <For each={product()!.specs}>
                          {spec => (
                            <div class="flex items-baseline gap-4 px-5 py-2.5">
                              <dt class="text-xs text-foreground-subtle w-36 shrink-0">{spec.label}</dt>
                              <dd class="text-xs text-foreground font-medium">{specLabel(spec)}</dd>
                            </div>
                          )}
                        </For>
                      </dl>
                    </Card.Body>
                  </Card>
                </div>
              </Show>

              <Show when={product()!.content}>
                <div>
                  <h2 class="text-lg font-semibold text-foreground mb-4">{isService() ? 'Sobre este servicio' : 'Descripción del producto'}</h2>
                  <div class="prose prose-sm max-w-none text-foreground-muted" innerHTML={product()!.content ?? ''} />
                </div>
              </Show>

              <Show when={product()!.steps.length > 0}>
                <div>
                  <h2 class="text-lg font-semibold text-foreground mb-4">Cómo trabajamos</h2>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <For each={product()!.steps}>
                      {(stepItem, index) => (
                        <div class="border border-border rounded-xl p-4">
                          <div class="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold mb-2">{index() + 1}</div>
                          <h3 class="text-sm font-semibold text-foreground">{stepItem.title}</h3>
                          <Show when={stepItem.description}>
                            <p class="text-xs text-foreground-muted mt-1 leading-relaxed">{stepItem.description}</p>
                          </Show>
                        </div>
                      )}
                    </For>
                  </div>
                </div>
              </Show>
            </div>

            {/* ── Derecha: buy box + seller (sticky) ── */}
            <div class="lg:sticky lg:top-24 lg:self-start space-y-4">
              {/* Buy Box */}
              <Card class="!rounded-xl">
                <Card.Body class="space-y-4">
                  <div class="flex items-center gap-2 text-xs text-foreground-subtle">
                    <Chip color="success" size="sm" variant="soft">
                      {isService() ? 'Servicio disponible' : 'En stock'}
                    </Chip>
                  </div>
                  <h1 class="text-lg font-semibold text-foreground leading-snug">{product()!.name}</h1>
                  <div class="flex items-center gap-2 text-xs">
                    <span class="text-foreground-subtle">4.8</span>
                    <span class="text-yellow-500">★★★★★</span>
                    <span class="text-foreground-subtle">(12)</span>
                  </div>
                  <div class="pt-1 space-y-1.5">
                    <Show when={product()!.compareAtPrice && product()!.compareAtPrice! > baseUnitPrice()}>
                      <div class="text-sm text-foreground-subtle line-through">{money(product()!.compareAtPrice!)}</div>
                    </Show>
                    <div class="flex items-end gap-2 flex-wrap">
                      <Show when={isService()}>
                        <span class="text-sm text-foreground-subtle pb-1">Desde</span>
                      </Show>
                      <span class="text-3xl font-bold text-foreground leading-none">{money(price())}</span>
                      <Show when={product()!.compareAtPrice && product()!.compareAtPrice! > price()}>
                        <span class="text-sm font-semibold text-success pb-0.5">{discountPercent(product()!.compareAtPrice!, price())}% OFF</span>
                      </Show>
                      <Show when={tier() && tier()!.name !== 'Público'}>
                        <Chip color="success" size="sm" variant="soft">
                          {tier()!.name} · {money(Number(tier()!.amount))} desde {tier()!.minQuantity}
                        </Chip>
                      </Show>
                      <Show when={price() < baseUnitPrice()}>
                        <span class="text-xs font-semibold text-success">{money(baseUnitPrice() - price())} menos por unidad</span>
                      </Show>
                    </div>
                    <Show when={!isService()}>
                      <p class="text-xs text-foreground-subtle">
                        / {product()!.unit} ·{' '}
                        <Show when={quantity() > 1} fallback={`${money(price())} c/u`}>
                          {quantity()} × {money(price())} = <span class="font-semibold text-foreground">{money(totalPrice())}</span>
                        </Show>
                      </p>
                    </Show>
                    <Show when={tiers().length > 1}>
                      <div class="flex flex-wrap gap-1.5 pt-1">
                        <For each={tiers()}>
                          {tr => (
                            <span
                              class={`text-xs px-2 py-1 rounded-full border ${tier()?.name === tr.name ? 'border-success bg-success-soft text-success font-semibold' : 'border-border text-foreground-subtle'}`}
                            >
                              {tr.name}: {money(Number(tr.amount))} <span class="opacity-70">· {tr.minQuantity}+</span>
                            </span>
                          )}
                        </For>
                      </div>
                    </Show>
                    <Show when={nextTier()}>
                      <p class="text-xs text-primary flex items-center gap-1">
                        <Icon icon="material-symbols-light:info-outline" size={14} />
                        Agrega {Number(nextTier()!.minQuantity) - quantity()} más y paga <span class="font-semibold">{money(Number(nextTier()!.amount))} c/u</span> ({nextTier()!.name})
                      </p>
                    </Show>
                  </div>
                  <Show when={product()!.attributes.length > 0}>
                    <div class="space-y-3">
                      <For each={product()!.attributes}>
                        {attribute => (
                          <div>
                            <p class="text-sm text-foreground mb-1.5">
                              {attribute.name}
                              <Show when={selection()[attribute.name]}>
                                <span class="text-foreground-subtle">: {selection()[attribute.name]}</span>
                              </Show>
                            </p>
                            <div class="flex flex-wrap gap-1.5">
                              <For each={attribute.values}>
                                {value => (
                                  <button
                                    type="button"
                                    onClick={() => setSelection(current => ({ ...current, [attribute.name]: value }))}
                                    class={`px-3 py-1.5 rounded-lg border text-sm transition-all ${selection()[attribute.name] === value ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-border text-foreground-subtle hover:border-foreground-subtle'}`}
                                  >
                                    {value}
                                  </button>
                                )}
                              </For>
                            </div>
                          </div>
                        )}
                      </For>
                      <Show when={selectedVariant()?.sku}>
                        <p class="text-[11px] text-foreground-subtle">SKU: {selectedVariant()!.sku}</p>
                      </Show>
                    </div>
                  </Show>
                  <Show when={stock() != null}>
                    <Show
                      when={stock()! > 0}
                      fallback={
                        <Chip color="danger" size="sm" variant="soft">
                          Sin existencias
                        </Chip>
                      }
                    >
                      <Chip color="success" size="sm" variant="soft">
                        {stock()} disponibles
                      </Chip>
                    </Show>
                  </Show>
                  <Show when={!isService()}>
                    <div class="space-y-2">
                      <div class="flex items-center gap-3">
                        <span class="text-sm text-foreground">Cantidad</span>
                        <div class="inline-flex items-center rounded-lg border border-border overflow-hidden">
                          <Button variant="text" color="default" size="sm" icon="material-symbols-light:remove" aria-label="Quitar uno" onClick={() => step(-1)} />
                          <span class="w-10 text-center text-sm font-semibold text-foreground">{quantity()}</span>
                          <Button variant="text" color="default" size="sm" icon="material-symbols-light:add" aria-label="Añadir uno" onClick={() => step(1)} />
                        </div>
                        <Show when={quantity() > 1}>
                          <span class="text-xs text-foreground-subtle">
                            Total <span class="font-semibold text-foreground">{money(totalPrice())}</span>
                          </span>
                        </Show>
                      </div>
                      <Show when={price() < baseUnitPrice()}>
                        <div class="flex items-center gap-2 rounded-lg border border-success/30 bg-success-soft px-3 py-2">
                          <Icon icon="material-symbols-light:local-offer" size={16} class="text-success" />
                          <p class="text-xs text-success">
                            ¡Precio <span class="font-semibold">{tier()?.name}</span> aplicado! Ahorras <span class="font-semibold">{money((baseUnitPrice() - price()) * quantity())}</span> en total.
                          </p>
                        </div>
                      </Show>
                    </div>
                  </Show>
                  <Show when={sentNumber()}>
                    <div class="flex items-center gap-2 rounded-lg border border-success/30 bg-success-soft px-3 py-2">
                      <Icon icon="material-symbols-light:check-circle" size={18} class="text-success shrink-0" />
                      <p class="text-xs text-success">
                        Solicitud <span class="font-semibold">{sentNumber()}</span> enviada. Te contactaremos pronto.
                      </p>
                    </div>
                  </Show>
                  <Show when={sinExistencias()}>
                    <p class="text-[11px] text-danger text-center">Sin existencias por ahora. Puedes pedir una cotización y te avisamos.</p>
                  </Show>
                  <Show when={avisoStock()}>
                    <p class="text-[11px] text-warning text-center">Ajustamos la cantidad a las existencias disponibles.</p>
                  </Show>
                  <div class="flex gap-2">
                    <Button
                      class="flex-1 min-w-0"
                      size="md"
                      color="primary"
                      prependIcon="material-symbols-light:bolt"
                      disabled={!puedeComprar()}
                      tooltip={missingSelection() ? 'Elige primero las opciones' : undefined}
                      onClick={handleBuyNow}
                    >
                      Comprar
                    </Button>
                    <Button
                      class="flex-1 min-w-0"
                      size="md"
                      variant="outlined"
                      color="primary"
                      prependIcon="material-symbols-light:add-shopping-cart"
                      disabled={!puedeComprar()}
                      onClick={handleAddToCart}
                    >
                      Agregar
                    </Button>
                  </div>
                  <Button
                    fullWidth
                    size="md"
                    variant="text"
                    color="default"
                    prependIcon="material-symbols-light:request-quote-outline"
                    disabled={missingSelection()}
                    tooltip={missingSelection() ? 'Elige primero las opciones' : undefined}
                    onClick={() => setShowQuote(true)}
                  >
                    Solicitar cotización
                  </Button>
                  <Show when={whatsappHref()}>
                    {href => (
                      <a href={href()} target="_blank" rel="noopener noreferrer" class="block">
                        <Button fullWidth size="md" variant="outlined" color="success" prependIcon="mdi:whatsapp">
                          Preguntar por WhatsApp
                        </Button>
                      </a>
                    )}
                  </Show>
                  <Show when={missingSelection()}>
                    <p class="text-[11px] text-warning text-center">
                      Elige{' '}
                      {product()!
                        .attributes.map(a => a.name.toLowerCase())
                        .join(' y ')}{' '}
                      para continuar
                    </p>
                  </Show>
                </Card.Body>
              </Card>

              {/* Seller */}
              <Card class="!rounded-xl">
                <Card.Body class="space-y-3">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon icon="material-symbols-light:storefront" size={20} class="text-primary" />
                    </div>
                    <div>
                      <p class="text-sm font-semibold text-foreground">{data()!.company.name}</p>
                      <p class="text-xs text-foreground-subtle">Tienda oficial</p>
                    </div>
                  </div>
                  <div class="flex items-center gap-4 text-xs text-foreground-subtle">
                    <span class="flex items-center gap-1">
                      <Icon icon="material-symbols-light:local-shipping" size={14} class="text-success" /> Envío disponible
                    </span>
                    <span class="flex items-center gap-1">
                      <Icon icon="material-symbols-light:verified" size={14} class="text-primary" /> Compra protegida
                    </span>
                  </div>
                  <Show when={whatsappHref()}>
                    {href => (
                      <a href={href()} target="_blank" rel="noopener noreferrer" class="block">
                        <Button fullWidth size="sm" variant="outlined" color="success" prependIcon="mdi:whatsapp">
                          Contactar por WhatsApp
                        </Button>
                      </a>
                    )}
                  </Show>
                </Card.Body>
              </Card>
            </div>
          </div>

          {/* ── Relacionados ── */}
          <Show when={(data()?.related.length ?? 0) > 0}>
            <section class="mt-10">
              <h2 class="text-lg font-semibold text-foreground mb-4">Productos relacionados</h2>
              <div class="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-thin">
                <For each={data()!.related}>{item => <RelatedCard empresa={empresa()} item={item} />}</For>
              </div>
            </section>
          </Show>
        </Container>

        <QuoteRequestModal
          isOpen={showQuote()}
          onClose={() => setShowQuote(false)}
          empresa={empresa()}
          productKey={product()!.key}
          productName={product()!.name}
          quantity={quantity()}
          variantId={selectedVariant()?.id ?? null}
          variantLabel={selectedVariant() ? optionsLabel(selectedVariant()!) : undefined}
          onSent={number => setSentNumber(number)}
        />

        <ImageViewer images={viewerImages()} open={viewerOpen()} initialIndex={imageIndex()} onClose={() => setViewerOpen(false)} onIndexChange={setImageIndex} showThumbnails loop />

        <CartDrawer empresa={empresa()} isOpen={showCart()} onClose={() => setShowCart(false)} formatPrice={value => money(value)} />

        {/*
          Acceso al carrito flotante: la barra superior es la de la web pública
          y no sabe de tiendas, así que el carrito se ofrece aquí, donde se
          compra. Sólo aparece cuando hay algo dentro.
        */}
        <Show when={cartCount(empresa()) > 0}>
          <button
            type="button"
            onClick={() => setShowCart(true)}
            aria-label={`Ver el carrito (${cartCount(empresa())} artículos)`}
            class="fixed bottom-24 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-primary-500 text-white shadow-lg transition hover:bg-primary-600"
          >
            <Icon icon="material-symbols-light:shopping-cart-outline" size={26} />
            <span class="absolute -right-1 -top-1 flex h-6 min-w-6 items-center justify-center rounded-full bg-foreground px-1.5 text-xs font-bold text-background">{cartCount(empresa())}</span>
          </button>
        </Show>
      </Show>
    </>
  )
}

const RelatedCard: Component<{ empresa: string; item: PublicProductType }> = props => (
  <A href={`/catalogo/${props.empresa}/${props.item.key}`} class="shrink-0 w-44 block group">
    <Card class="!rounded-xl">
      <Card.Body class="p-0">
        <div class="aspect-square bg-white flex items-center justify-center overflow-hidden p-2">
          <Show when={props.item.images[0]} fallback={<Icon icon="material-symbols-light:image-outline" size={32} class="text-foreground-subtle/30" />}>
            <img src={props.item.images[0]} alt={props.item.name} class="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300" />
          </Show>
        </div>
        <div class="px-3 pb-3 pt-1">
          <p class="text-xs text-foreground truncate group-hover:text-primary transition-colors leading-snug">{props.item.name}</p>
          <p class="text-sm font-bold text-foreground mt-1">{money(props.item.price)}</p>
        </div>
      </Card.Body>
    </Card>
  </A>
)

export default ProductPublicPage
